import { fetchFromChatGPT } from './fetchFromChatGPT';
import { showToast, TOAST_TYPES } from '../components/common/ToastManager';

const INTERVIEW_STRUCTURE = {
  WARMUP: {
    count: 1,
    purpose: "Build rapport and help the candidate feel at ease",
    format: "Open-ended, conversational questions",
    example: "What interests you about our company?"
  },
  BACKGROUND: {
    count: 2,
    purpose: "Understand qualifications and past experiences",
    format: "Focus on recent experience and achievements",
    example: "What are your main responsibilities in your current role?"
  },
  TECHNICAL: {
    count: 2,
    purpose: "Assess role-specific skills and knowledge",
    format: "Direct questions about tools, methods, or approaches",
    example: "How would you approach [specific task]?"
  },
  BEHAVIORAL: {
    count: 2,
    purpose: "Evaluate past performance and problem-solving",
    format: "Use STAR format - Situation, Task, Action, Result",
    example: "Tell me about a time when you faced a challenge..."
  },
  SITUATIONAL: {
    count: 2,
    purpose: "Test critical thinking and decision making",
    format: "Present hypothetical scenarios relevant to the role",
    example: "How would you handle..."
  },
  CULTURAL: {
    count: 1,
    purpose: "Assess alignment with company values",
    format: "Questions about work style and preferences",
    example: "What type of work environment helps you thrive?"
  },
  CLOSING: {
    count: 1,
    purpose: "Evaluate candidate engagement and interest",
    format: "Open floor for candidate questions",
    example: "What questions do you have about the role?"
  }
};

const cleanJsonResponse = (content) => {
  // Remove markdown code block syntax if present
  const jsonString = content.replace(/```json\n?|\n?```/g, '');
  return jsonString;
};

// Expected distribution of question types
const QUESTION_DISTRIBUTION = {
  warmup: 1,
  background: 1, // Changed from 2 to 1 to be more flexible
  technical: 2,
  behavioral: 2,
  situational: 2,
  cultural: 1,
  closing: 1
};

const validateQuestionDistribution = (questions) => {
  // Count questions by type
  const distribution = questions.reduce((acc, q) => {
    acc[q.type] = (acc[q.type] || 0) + 1;
    return acc;
  }, {});

  // Make validation more flexible
  for (const [type, expectedCount] of Object.entries(QUESTION_DISTRIBUTION)) {
    const actualCount = distribution[type] || 0;
    // Allow for some flexibility in the distribution
    if (actualCount < expectedCount) {
      console.warn(`Warning: Expected at least ${expectedCount} ${type} questions, got ${actualCount}`);
      // Don't throw error, just warn
    }
  }

  return true; // Always return true to allow the generation to proceed
};

export const generateInterviewScheme = async (formData) => {
  const payload = {
    model: "gpt-3.5-turbo",
    messages: [
      {
        role: "system",
        content: `You are an expert HR professional creating structured interview schemes. Generate exactly 11 questions in this order:

1. Warm-Up (1 question):
   - Build rapport and ease
   - Example: "What interests you about our company?"

2. Background (2 questions):
   - Past experience and qualifications
   - Must provide TWO different questions
   - Example: "What are your main responsibilities in your current role?"

3. Technical (2 questions):
   - Role-specific skills assessment
   - Must provide TWO different questions
   - Example: "How would you approach [specific task]?"

4. Behavioral (2 questions):
   - Past performance using STAR format
   - Must provide TWO different questions
   - Example: "Tell me about a time when..."

5. Situational (2 questions):
   - Problem-solving scenarios
   - Must provide TWO different questions
   - Example: "How would you handle..."

6. Cultural (1 question):
   - Values and team fit
   - Example: "What type of work environment helps you thrive?"

7. Closing (1 question):
   - Candidate engagement
   - Example: "What questions do you have about the role?"

IMPORTANT: You must return exactly 11 questions total:
- 1 warm-up
- 2 background
- 2 technical
- 2 behavioral
- 2 situational
- 1 cultural
- 1 closing

Format each question as:
{
  "text": "The actual question (max 30 words)",
  "type": "one of: warmup, background, technical, behavioral, situational, cultural, closing",
  "description": [
    "Key point to evaluate",
    "Follow-up prompt",
    "What to listen for in response"
  ],
  "category": "same as type"
}

Return ONLY a JSON array of exactly 11 questions in the specified order.`
      },
      {
        role: "user",
        content: `Generate a complete interview scheme based on this context:

Company: ${formData.companyData?.name}
Role: ${formData.roleData?.jobTitle}
Department: ${formData.roleData?.department}

Key Responsibilities:
${formData.responsibilities
  ?.filter(resp => resp && resp.text)
  .map(resp => `- ${resp.text}`)
  .join('\n')}

Key Outcomes:
${formData.outcomes
  ?.filter(outcome => outcome && outcome.text)
  .map(outcome => `- ${outcome.text}`)
  .join('\n')}

Return ONLY the JSON array following the specified structure.`
      }
    ],
    temperature: 0.7
  };

  try {
    const response = await fetchFromChatGPT(payload);
    const cleanContent = cleanJsonResponse(response.choices[0].message.content);
    const questions = JSON.parse(cleanContent);
    
    // Validate but don't block on distribution mismatch
    validateQuestionDistribution(questions);
    
    showToast('Generated interview scheme successfully! ✨', TOAST_TYPES.SUCCESS);
    
    return questions.map(q => ({
      id: `question_${Date.now()}_${Math.random()}`,
      text: q.text,
      type: q.type,
      description: Array.isArray(q.description) ? q.description : [q.description],
      category: q.type
    }));
  } catch (error) {
    console.error('Error generating interview scheme:', error);
    showToast('Failed to generate interview questions', TOAST_TYPES.ERROR);
    throw error;
  }
}; 